import React from 'react';
import { Modal } from '@mantine/core';
import { Button } from '../../../components/Button';
import { LOG_LEVEL_LABELS, LOG_PLACE_LABELS } from '../monitoring-page.constants';
import { formatDateTime } from '../monitoring-page.utils';
import type { UseMonitoringPage } from '../useMonitoringPage';
import classes from '../monitoring-page.module.scss';

type ErrorDetailModalProps = Pick<
  UseMonitoringPage,
  'selectedError' | 'closeErrorDetail' | 'ignoreSelectedError' | 'unignoreSelectedError' | 'isSavingIgnore'
>;

const resolvePlaceLabel = (place: unknown): string => {
  if (place === 'backend' || place === 'frontend') {
    return LOG_PLACE_LABELS[place];
  }
  return '-';
};

const resolveLevelLabel = (level: unknown): string => {
  if (level === 'error' || level === 'warn' || level === 'info' || level === 'debug') {
    return LOG_LEVEL_LABELS[level];
  }
  return '-';
};

export const ErrorDetailModal: React.FC<ErrorDetailModalProps> = ({
  selectedError,
  closeErrorDetail,
  ignoreSelectedError,
  unignoreSelectedError,
  isSavingIgnore,
}) => {
  const row = selectedError;

  const fields: { label: string; value: string }[] = row
    ? [
        { label: 'מקור', value: resolvePlaceLabel(row.place) },
        { label: 'חומרה', value: resolveLevelLabel(row.level) },
        { label: 'תאריך ושעה', value: formatDateTime(row.createdAt) },
        { label: 'עסק', value: row.companyName ?? row.tenantId ?? '-' },
        { label: 'משמרת', value: row.sessionLabel ?? '-' },
        { label: 'מלצר', value: row.employeeName ?? '-' },
        { label: 'מכשיר', value: row.deviceId ?? '-' },
        { label: 'נתיב', value: row.route ?? '-' },
        { label: 'שיטת HTTP', value: row.httpMethod ?? '-' },
        { label: 'סטטוס HTTP', value: row.httpStatus != null ? String(row.httpStatus) : '-' },
        { label: 'מזהה בקשה', value: row.requestId ?? '-' },
        { label: 'גרסה', value: row.appVersion ?? '-' },
        { label: 'דפדפן', value: row.userAgent ?? '-' },
      ]
    : [];

  return (
    <Modal
      opened={row != null}
      onClose={closeErrorDetail}
      title={row ? (row.errorName ? `${row.errorName}: ${row.message}` : row.message) : ''}
      centered
      size="xl"
      closeButtonProps={{ 'data-testid': 'monitoring-errors-detail-close' } as Record<string, string>}
    >
      {row && (
        <div className={classes.errorDetail} data-testid="monitoring-errors-detail">
          <div className={classes.detailsSection}>
            {fields.map((field) => (
              <div key={field.label} className={classes.detailField}>
                <span className={classes.detailLabel}>{field.label}</span>
                <span className={classes.detailValue}>{field.value}</span>
              </div>
            ))}
          </div>

          <div className={classes.sectionTitle}>{'trace'}</div>
          <pre className={classes.errorTrace}>{row.trace ?? '—'}</pre>

          {row.context && (
            <>
              <div className={classes.sectionTitle}>{'הקשר'}</div>
              <pre className={classes.errorTrace}>{JSON.stringify(row.context, null, 2)}</pre>
            </>
          )}

          <div className={classes.filtersActions}>
            {row.isIgnored ? (
              <Button
                variant="confirm"
                onClick={() => void unignoreSelectedError()}
                loading={isSavingIgnore}
                dataTestId="monitoring-errors-detail-unignore"
              >
                {'בטל התעלמות'}
              </Button>
            ) : (
              <Button
                variant="cancel"
                onClick={() => void ignoreSelectedError()}
                loading={isSavingIgnore}
                dataTestId="monitoring-errors-detail-ignore"
              >
                {'התעלם לצמיתות'}
              </Button>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
};
